<script setup lang="ts">
import { computed, onBeforeUnmount, ref } from 'vue'

export interface WheelSegment {
  label: string
  tone: 'forest' | 'pine' | 'ivory' | 'sand'
}

const props = defineProps<{
  segments: WheelSegment[]
  disabled?: boolean
}>()

const emit = defineEmits<{
  (e: 'go'): void
  (e: 'done'): void
}>()

// ---- Geometry (SVG units, viewBox 0..400) --------------------------------------
const CENTER = 200
const RADIUS = 186 // outer edge of the wedges
const LABEL_RADIUS = 118
const BULB_RADIUS = 192

// ---- Motion profile -------------------------------------------------------------
// Total spin time, and the phase split of the normalised timeline (0..1):
//   0.00 - 0.12  anticipation: the disc pulls back a few degrees, like a watch escapement
//   0.12 - 0.27  acceleration: velocity ramps linearly from 0 to the peak
//   0.27 - 0.60  constant speed: the "roar"
//   0.60 - 1.00  ease-out: velocity falls linearly to 0, so the stop has no hard edge
const SPIN_DURATION_MS = 6200
const PULL_BACK_DEG = 7
const PULL_END = 0.12
const RAMP_END = 0.27
const PLATEAU_END = 0.6
const EXTRA_TURNS = 6

// Peak velocity V (normalised distance per unit time) so that the three
// motion phases add up to exactly 1 of normalised distance:
//   ramp distance      = V * RAMP_END / 2
//   plateau distance   = V * (PLATEAU_END - RAMP_END)
//   decel distance     = V * (1 - PLATEAU_END) / 2
const PEAK_V =
  1 / (RAMP_END / 2 + (PLATEAU_END - RAMP_END) + (1 - PLATEAU_END) / 2)

// Normalised position in [0, 1] for a main-phase progress p in [0, 1].
function mainProgress(p: number): number {
  const decelLength = 1 - PLATEAU_END
  if (p <= RAMP_END) {
    // constant acceleration: distance = V * p^2 / (2 * RAMP_END)
    return (PEAK_V * p * p) / (2 * RAMP_END)
  }
  if (p <= PLATEAU_END) {
    return PEAK_V * (RAMP_END / 2 + (p - RAMP_END))
  }
  const q = p - PLATEAU_END
  return PEAK_V * (RAMP_END / 2 + (PLATEAU_END - RAMP_END) + q - (q * q) / (2 * decelLength))
}

// Anticipation offset in degrees for timeline progress t in [0, 1].
// Goes 0 -> -PULL_BACK_DEG -> 0 over the first PULL_END of the timeline.
function pullOffset(t: number): number {
  if (t >= PULL_END) return 0
  return -PULL_BACK_DEG * Math.sin((Math.PI * t) / PULL_END)
}

// ---- Wheel state ----------------------------------------------------------------
const rotation = ref(0)
const spinning = ref(false)
let frameId = 0
let startedAt = 0
let fromAngle = 0
let travel = 0
let reduce = false

const step = computed(() => 360 / props.segments.length)
const locked = computed(() => spinning.value || !!props.disabled)

const TONES: Record<WheelSegment['tone'], { fill: string; text: string }> = {
  forest: { fill: '#143D30', text: '#FAF6F0' },
  pine: { fill: '#1E5141', text: '#FAF6F0' },
  ivory: { fill: '#FAF6F0', text: '#143D30' },
  sand: { fill: '#E9DDC3', text: '#143D30' },
}

// Angles are measured clockwise from 12 o'clock, where the pointer sits.
// Cartesian point for an angle a (deg) at radius r:
//   x = CENTER + r * sin(a)
//   y = CENTER - r * cos(a)
function polar(angle: number, radius: number) {
  const rad = (angle * Math.PI) / 180
  return { x: CENTER + radius * Math.sin(rad), y: CENTER - radius * Math.cos(rad) }
}

function wedgePath(index: number) {
  const start = polar(index * step.value, RADIUS)
  const end = polar((index + 1) * step.value, RADIUS)
  const largeArc = step.value > 180 ? 1 : 0
  return `M ${CENTER} ${CENTER} L ${start.x} ${start.y} A ${RADIUS} ${RADIUS} 0 ${largeArc} 1 ${end.x} ${end.y} Z`
}

// Long labels are broken into two lines at the space nearest the middle,
// so they fit the wedge length without shrinking the type.
const MAX_ONE_LINE = 9

function splitLabel(text: string): string[] {
  if (text.length <= MAX_ONE_LINE) return [text]
  const words = text.split(' ')
  if (words.length < 2) return [text]
  let best = 1
  let bestGap = Infinity
  for (let i = 1; i < words.length; i++) {
    const first = words.slice(0, i).join(' ')
    const second = words.slice(i).join(' ')
    const gap = Math.abs(first.length - second.length)
    if (gap < bestGap) {
      bestGap = gap
      best = i
    }
  }
  return [words.slice(0, best).join(' '), words.slice(best).join(' ')]
}

const wedges = computed(() =>
  props.segments.map((segment, index) => {
    // Centre of wedge i sits at (i + 0.5) * step degrees, clockwise from 12 o'clock.
    const mid = (index + 0.5) * step.value
    const anchor = polar(mid, LABEL_RADIUS)
    // Text baseline follows the radius. Right half (0-180 deg): read outward
    // from the hub, rotate by (mid - 90). Left half: read inward, rotate by
    // (mid + 90). Either way the letter tops face up, never upside down.
    const rotate = mid >= 180 ? mid + 90 : mid - 90
    const lines = splitLabel(segment.label)
    return {
      path: wedgePath(index),
      fill: TONES[segment.tone].fill,
      textColor: TONES[segment.tone].text,
      anchorX: anchor.x,
      anchorY: anchor.y,
      rotate,
      lines: lines.map((text, i) => ({
        text,
        // Line spacing in the text's own (rotated) frame: centred on the anchor.
        dy: lines.length === 1 ? '0.35em' : i === 0 ? '-0.55em' : '1.15em',
      })),
    }
  })
)

const bulbs = computed(() =>
  Array.from({ length: 24 }, (_, i) => {
    const p = polar((i * 360) / 24, BULB_RADIUS)
    return { x: p.x, y: p.y, delay: `${(i % 6) * 0.35}s` }
  })
)

// ---- Spin --------------------------------------------------------------------------
function frame(now: number) {
  const elapsed = now - startedAt
  const t = Math.min(elapsed / SPIN_DURATION_MS, 1)

  let angle: number
  if (t < PULL_END) {
    angle = fromAngle + pullOffset(t)
  } else {
    const p = (t - PULL_END) / (1 - PULL_END)
    angle = fromAngle + travel * mainProgress(p)
  }
  rotation.value = angle

  if (t < 1) {
    frameId = requestAnimationFrame(frame)
  } else {
    rotation.value = fromAngle + travel
    spinning.value = false
    emit('done')
  }
}

/**
 * Turn the wheel so that wedge `index` stops under the pointer.
 *
 * Wedge centre angle            m = (index + 0.5) * step
 * Pointer is at 0 deg, so the disc must end at R where (m + R) mod 360 = 0,
 *   i.e. R mod 360 = (360 - m) mod 360.
 * A random jitter of +/- 30% of a wedge keeps landings from always looking identical.
 */
function spinTo(index: number) {
  // Only a turn already in progress blocks this. The parent sets `disabled` before calling
  // spinTo (while the wheel is "spinning"), so checking `disabled` here would refuse every spin.
  if (spinning.value) return

  const jitter = (Math.random() - 0.5) * step.value * 0.6
  const centre = (index + 0.5) * step.value + jitter
  const wantModulo = (360 - centre + 360) % 360
  const currentModulo = ((rotation.value % 360) + 360) % 360
  // Forward distance to the wanted angle, always in [0, 360).
  const forward = (((wantModulo - currentModulo) % 360) + 360) % 360

  fromAngle = rotation.value
  travel = forward + EXTRA_TURNS * 360
  spinning.value = true

  reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches
  if (reduce) {
    rotation.value = fromAngle + travel
    spinning.value = false
    emit('done')
    return
  }

  startedAt = performance.now()
  frameId = requestAnimationFrame(frame)
}

function onGo() {
  if (locked.value) return
  emit('go')
}

onBeforeUnmount(() => cancelAnimationFrame(frameId))

defineExpose({ spinTo })
</script>

<template>
  <div class="spin-wheel" :class="{ 'is-spinning': spinning }">
    <!-- Marquee lamps: warm champagne bulbs that breathe, faster while the wheel turns. -->
    <svg class="spin-wheel__lamps" viewBox="0 0 400 400" aria-hidden="true">
      <circle v-for="(bulb, i) in bulbs" :key="i" class="lamp" :cx="bulb.x" :cy="bulb.y" r="4.2" :style="{ animationDelay: bulb.delay }" />
    </svg>

    <svg class="spin-wheel__disc" viewBox="0 0 400 400" role="img" aria-label="Prize wheel">
      <defs>
        <linearGradient id="sw-bezel" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stop-color="#F3E2B3" />
          <stop offset="45%" stop-color="#DFBA73" />
          <stop offset="70%" stop-color="#9C7A3C" />
          <stop offset="100%" stop-color="#E9CF8E" />
        </linearGradient>
      </defs>

      <g class="spin-wheel__rotor" :style="{ transform: `rotate(${rotation}deg)` }">
        <circle :cx="CENTER" :cy="CENTER" :r="RADIUS + 5" fill="url(#sw-bezel)" />
        <g v-for="(wedge, index) in wedges" :key="index">
          <path :d="wedge.path" :fill="wedge.fill" stroke="#DFBA73" stroke-width="1" />
          <text
            class="spin-wheel__label"
            :style="{ fontSize: segments.length >= 12 ? '14px' : segments.length >= 8 ? '16px' : '18px' }"
            :x="wedge.anchorX"
            :y="wedge.anchorY"
            :fill="wedge.textColor"
            text-anchor="middle"
            :transform="`rotate(${wedge.rotate} ${wedge.anchorX} ${wedge.anchorY})`"
          >
            <tspan
              v-for="(line, i) in wedge.lines"
              :key="i"
              :x="wedge.anchorX"
              :dy="line.dy"
            >{{ line.text }}</tspan>
          </text>
        </g>
      </g>
    </svg>

    <!-- Pointer: a small champagne-gold blade at 12 o'clock. -->
    <div class="spin-wheel__pointer" aria-hidden="true" />

    <!-- Hub: watch-dial bezel with a brushed-metal face. The GO button sits on top. -->
    <button
      type="button"
      class="spin-wheel__hub"
      :disabled="locked"
      :aria-busy="spinning ? 'true' : 'false'"
      aria-label="Explore and spin the wheel"
      @click="onGo"
    >
      <span class="spin-wheel__hub-label">Explore</span>
    </button>
  </div>
</template>

<style scoped>
.spin-wheel {
  position: relative;
  /* Fill the column it sits in (the modal's left column, or the full width on mobile). */
  width: min(100%, 380px);
  aspect-ratio: 1;
  margin: 0 auto;
  --forest: #143d30;
  --champagne: #dfba73;
  --ivory: #faf6f0;
}

.spin-wheel__disc,
.spin-wheel__lamps {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
}

.spin-wheel__rotor {
  will-change: transform;
  transform-origin: 200px 200px;
}

/* Prize labels: heavy display weight so they read at a glance on the wheel */
.spin-wheel__label {
  font-family: 'Poppins', system-ui, sans-serif;
  font-size: 18px;
  font-weight: 800;
  letter-spacing: 0.03em;
  paint-order: stroke;
  stroke: rgb(0 0 0 / 0.12);
  stroke-width: 0.6px;
}

/* Lamps */
.lamp {
  fill: #ffe7a8;
  filter: drop-shadow(0 0 4px rgb(223 186 115 / 0.85));
  animation: lamp-breathe 3.2s ease-in-out infinite;
}

.is-spinning .lamp {
  animation-duration: 0.9s;
}

@keyframes lamp-breathe {
  0%,
  100% {
    opacity: 0.25;
  }
  50% {
    opacity: 1;
  }
}

/* Pointer */
.spin-wheel__pointer {
  position: absolute;
  top: -6px;
  left: 50%;
  z-index: 2;
  width: 0;
  height: 0;
  transform: translateX(-50%);
  border-left: 14px solid transparent;
  border-right: 14px solid transparent;
  border-top: 34px solid var(--champagne);
  filter: drop-shadow(0 2px 3px rgb(0 0 0 / 0.45));
}

/* Hub: GO button with a brushed-metal face */
.spin-wheel__hub {
  position: absolute;
  top: 50%;
  left: 50%;
  z-index: 3;
  width: 30%;
  aspect-ratio: 1;
  transform: translate(-50%, -50%);
  border: 3px solid var(--champagne);
  border-radius: 50%;
  background:
    repeating-linear-gradient(90deg, rgb(255 255 255 / 0.05) 0 1px, transparent 1px 3px),
    linear-gradient(145deg, #f3e2b3 0%, #dfba73 38%, #9c7a3c 62%, #e9cf8e 100%);
  box-shadow:
    inset 0 1px 0 rgb(255 255 255 / 0.6),
    0 6px 18px rgb(0 0 0 / 0.4);
  color: var(--forest);
  font: inherit;
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.2s ease;
}

.spin-wheel__hub:hover:not(:disabled) {
  box-shadow:
    inset 0 1px 0 rgb(255 255 255 / 0.6),
    0 8px 24px rgb(223 186 115 / 0.35);
}

.spin-wheel__hub:active:not(:disabled) {
  transform: translate(-50%, -50%) scale(0.97);
}

.spin-wheel__hub:focus-visible {
  outline: 3px solid var(--ivory);
  outline-offset: 4px;
}

.spin-wheel__hub:disabled {
  cursor: default;
  opacity: 0.85;
}

.spin-wheel__hub-label {
  font-size: 16.8px;
  font-weight: 700;
  letter-spacing: 0.22em;
  text-transform: uppercase;
}

@media (prefers-reduced-motion: reduce) {
  .lamp {
    animation: none;
    opacity: 0.8;
  }
}
</style>
